'use client';

import { createContext, useContext, useMemo } from 'react';

import { Types } from '@scorenco/core';

export const CompetitionRankingContext = createContext<
  CompetitionRankingContextProps | undefined
>(undefined);

export const useCompetitionRankingContext =
  (): CompetitionRankingContextProps => {
    const context = useContext(CompetitionRankingContext);
    if (!context) {
      throw new Error(
        'useCompetitionRankingContext must be inside a CompetitionRanking'
      );
    }
    return context;
  };

export type CompetitionRankingContextProps = {
  columns: string[];
  competitionId?: number;
  teamWidth: number;
  infoWidth: number;
  highlightTeams?: number[];
  isClubhouse?: boolean;
};

export enum RankingProp {
  pts = 'pts',
  even = 'even',
  ga = 'ga',
  gd = 'gd',
  gf = 'gf',
  lost = 'lost',
  played = 'played',
  rank = 'rank',
  ratio = 'ratio',
  win = 'win',
  gnp = 'gnp',
  gp = 'gp',
  serie = 'serie',
  sets = 'sets',
  sets_g = 'sets_g',
  sets_a = 'sets_a',
}

export const useInitializeContext = ({
  sport,
  highlightTeams,
  competitionId,
  isClubhouse = false,
}: {
  sport?: Types.Clubs_Sport;
  highlightTeams?: number[];
  competitionId?: number;
  isClubhouse?: boolean;
}): CompetitionRankingContextProps => {
  const teamWidth = 12.75;
  const { columns, infoWidth } = useMemo(() => {
    if (!sport) {
      return { columns: [], infoWidth: 0 };
    }

    return {
      columns: [
        RankingProp.pts,
        RankingProp.played,
        sport.slug === 'basket' || sport.slug === 'volley'
          ? RankingProp.gp
          : RankingProp.gnp,
        sport.slug === 'volley' && RankingProp.sets,
        sport.slug === 'volley' ? RankingProp.sets_g : RankingProp.gf,
        sport.slug === 'volley' ? RankingProp.sets_a : RankingProp.ga,
        sport.slug !== 'volley' && RankingProp.gd,
        !isClubhouse && sport.slug !== 'volley' && RankingProp.ratio,
        !isClubhouse && RankingProp.serie,
      ].filter(Boolean) as string[],
      infoWidth: [
        //RankingProp.pts,
        2,
        //RankingProp.played,
        2,
        //RankingProp.gp ou RankingProp.gnp,
        3.4,
        // RankingProp.sets,
        sport.slug === 'volley' && 6,
        // RankingProp.gf,
        2,
        // RankingProp.ga,
        2,
        // RankingProp.gd,
        sport.slug !== 'volley' && 2,
        // RankingProp.ratio,
        !isClubhouse && sport.slug !== 'volley' && 2,
        // RankingProp.serie,
        !isClubhouse && 6,
      ]
        .filter(Boolean)
        .reduce((acc, v) => Number(acc) + Number(v), 0) as number,
    };
  }, [sport, isClubhouse]);

  return {
    columns,
    teamWidth,
    infoWidth,
    highlightTeams,
    competitionId,
    isClubhouse,
  };
};
